import type { Uuid } from "@shared/api";

/** Место в ряду серверов: сервер или папка — как в Discord. */
export type RailItem =
  | { kind: "server"; id: Uuid }
  | { kind: "folder"; id: Uuid; name: string | null; color: string | null; servers: Uuid[] };

export type Folder = Extract<RailItem, { kind: "folder" }>;

/** Куда положить сервер: перед или после места, или в него (в папку). */
export type Drop = { how: "before" | "after" | "into"; at: Uuid };

/**
 * Ряд как его рисовать: сохранённая раскладка без серверов, из которых ушли,
 * и с новыми — в конце, в порядке, в каком их отдаёт сервер.
 */
export function arrange(layout: readonly RailItem[], servers: readonly Uuid[]): RailItem[] {
  const alive = new Set(servers);
  const placed = new Set<Uuid>();
  const out: RailItem[] = [];

  for (const item of layout) {
    if (item.kind === "server") {
      if (alive.has(item.id) && !placed.has(item.id)) {
        placed.add(item.id);
        out.push(item);
      }
      continue;
    }
    const inside = item.servers.filter((id) => alive.has(id) && !placed.has(id));
    inside.forEach((id) => placed.add(id));
    if (inside.length > 0) out.push({ ...item, servers: inside });
  }

  for (const id of servers) if (!placed.has(id)) out.push({ kind: "server", id });
  return out;
}

/** Где сервер: в корне или в папке. */
export function folderOf(layout: readonly RailItem[], id: Uuid): Folder | null {
  return (layout.find((item) => item.kind === "folder" && item.servers.includes(id)) as Folder | undefined) ?? null;
}

function without(layout: readonly RailItem[], id: Uuid): RailItem[] {
  return layout
    .filter((item) => !(item.kind === "server" && item.id === id))
    .map((item) => (item.kind === "folder" ? { ...item, servers: item.servers.filter((server) => server !== id) } : item))
    .filter((item) => item.kind === "server" || item.servers.length > 0);
}

/**
 * Перетащили сервер — как в Discord: на край места — встать рядом, в
 * середину сервера — сложить оба в новую папку, в середину папки — в неё.
 */
export function moveServer(layout: readonly RailItem[], id: Uuid, drop: Drop, newId: () => Uuid): RailItem[] {
  if (drop.at === id) return [...layout];
  const rest = without(layout, id);

  const root = rest.findIndex((item) => item.id === drop.at);
  if (root >= 0) {
    const target = rest[root];
    if (drop.how !== "into") {
      rest.splice(drop.how === "before" ? root : root + 1, 0, { kind: "server", id });
      return rest;
    }
    rest[root] =
      target.kind === "folder"
        ? { ...target, servers: [...target.servers, id] }
        : { kind: "folder", id: newId(), name: null, color: null, servers: [target.id, id] };
    return rest;
  }

  // Цель внутри папки — сервер встаёт в ту же папку.
  return rest.map((item) => {
    if (item.kind !== "folder" || !item.servers.includes(drop.at)) return item;
    const servers = [...item.servers];
    const at = servers.indexOf(drop.at);
    servers.splice(drop.how === "before" ? at : at + 1, 0, id);
    return { ...item, servers };
  });
}

/** Папку перетащили — она встаёт перед или после места в корне. */
export function moveFolder(layout: readonly RailItem[], folderId: Uuid, drop: Drop): RailItem[] {
  const folder = layout.find((item) => item.id === folderId);
  if (!folder || drop.at === folderId) return [...layout];
  const rest = layout.filter((item) => item.id !== folderId);
  // Цель в папке — встаём рядом с той папкой: папки в папках не бывает.
  const host = folderOf(rest, drop.at)?.id ?? drop.at;
  const at = rest.findIndex((item) => item.id === host);
  if (at < 0) return [...layout];
  rest.splice(drop.how === "before" ? at : at + 1, 0, folder);
  return rest;
}

/** Вынуть сервер из папки — он встаёт сразу после неё. */
export function takeOut(layout: readonly RailItem[], id: Uuid): RailItem[] {
  const folder = folderOf(layout, id);
  if (!folder) return [...layout];
  const rest = without(layout, id);
  const at = rest.findIndex((item) => item.id === folder.id);
  rest.splice(at < 0 ? rest.length : at + 1, 0, { kind: "server", id });
  return rest;
}

/** Распустить папку: её серверы встают на её место по порядку. */
export function ungroup(layout: readonly RailItem[], folderId: Uuid): RailItem[] {
  return layout.flatMap((item) =>
    item.kind === "folder" && item.id === folderId
      ? item.servers.map((id) => ({ kind: "server" as const, id }))
      : [item],
  );
}

/** Новая папка из одного сервера — на его месте. */
export function newFolder(layout: readonly RailItem[], id: Uuid, newId: () => Uuid): RailItem[] {
  return layout.map((item) =>
    item.kind === "server" && item.id === id
      ? { kind: "folder", id: newId(), name: null, color: null, servers: [id] }
      : item,
  );
}

export function editFolder(
  layout: readonly RailItem[],
  folderId: Uuid,
  change: { name?: string | null; color?: string | null },
): RailItem[] {
  return layout.map((item) => (item.kind === "folder" && item.id === folderId ? { ...item, ...change } : item));
}
